import { computed, inject, Injectable, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import Collection from '@arcgis/core/core/Collection';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point.js';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import EsriMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';

import {
  ErrorHandlerOptions,
  EsriErrorHandler,
  EsriRequestError,
  EsriViewType,
  ExtendedMap,
  JepBasemap,
  JepGroupLayer,
  JepHitTestResult,
  JepLayer,
  JmkwApiInterface,
  JmkwEvent,
  JmkwThemeConfig,
  JmkwUnitConfig,
  JmkwUnitConfigKey,
  Plugin,
  PluginID,
  PluginMap,
  PurlySignedUrlInfo,
  ThemeInfo,
  ViewConfig,
  WidgetParams
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import {
  filter,
  from,
  map,
  Observable,
  shareReplay,
  Subscriber,
  switchMap,
  take,
  throwError
} from 'rxjs';
import { store } from './jmkw.store';
import { JMKW_VERSION } from './lib/version';
import {
  AvailableBasemaps,
  BasemapKey,
  BasemapManagerService,
  EsriInterceptor,
  HitTestService
} from './lib/arcgis';
import { EventBusService } from './lib/events/event-bus.service';
import { PurlyService } from './lib/services/purly.service';
import { ThemeService } from './lib/theme';
import type { GoToOptions3D, GoToOptions2D } from "@arcgis/core/views/types";

@Injectable({
  providedIn: 'root'
})
export class JmkwApiService implements JmkwApiInterface {
  private readonly _eventBusService = inject(EventBusService);
  private readonly _hitTestService = inject(HitTestService);
  private readonly _logger = inject(NGXLogger);
  private readonly _themeService = inject(ThemeService);
  private readonly _basemapManagerService = inject(BasemapManagerService);
  private readonly _esriInterceptor = inject(EsriInterceptor);
  private readonly _purlyService = inject(PurlyService);

  // This is the latitude of the edge of the visible map
  private readonly _edgeOfMap = 85.05103;
  private readonly _goToOptions = { duration: 1 };
  private readonly _map$: Observable<EsriMap>;

  constructor() {
    this._map$ = toObservable(store.map);
  }

  getPlugins(): PluginMap {
    return store.pluginMap();
  }

  getPluginById(id: PluginID): Plugin | undefined {
    return store.pluginMap()?.get(id);
  }

  onLayerReady$<T extends JepLayer>(id: string): Observable<T> {
    return this.mapReady$.pipe(
      switchMap(() => this._createLayerReadyObservable<T>(id)),
      take(1),
      shareReplay(1)
    );
  }

  findPlugin(partialPlugin: Partial<Plugin>): Plugin | null {
    const found = store.pluginMap()?.find(partialPlugin);
    return found?.plugin ?? null; // Handle undefined case
  }

  getLayerById<LayerType extends JepLayer>(
    id?: PluginID
  ): LayerType | undefined {
    if (!id) return undefined;
    const plugin = this.getPluginById(id);
    if (plugin?.layer) {
      return plugin.layer as LayerType;
    }

    // Fallback to direct layer search
    const layerResult = this.findLayer({ id });
    const layer = layerResult.layer as LayerType;

    if (!layer) {
      this._logger.debug(`[JmkwApiService] Layer with id "${id}" not found.`);
      this._logger.debug('[JmkwApiService] Available layers:', this.getMapOfLayers());
      return;
    }

    return layer;
  }

  private _createLayerReadyObservable<T extends JepLayer>(
    id: string
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      const layerWatch = reactiveUtils.watch(
        () => {
          const collection = this.getLayerCollection();
          if (!collection) return undefined;

          // Handle the case where find returns undefined
          return collection.find((layer) => layer?.id === id) as T;
        },
        (layer?: T) => this._onLayerFound(layer, subscriber),
        { initial: true }
      );

      return () => layerWatch?.remove();
    });
  }

  private _onLayerFound<T extends JepLayer>(
    layer: T | undefined, // Explicitly handle undefined
    subscriber: Subscriber<JepLayer> // Fix the generic type
  ): void {
    if (!layer) return;

    layer.when(() => {
      layer.on('layerview-create', () => {
        subscriber.next(layer);
      });
      if (layer.loaded) {
        subscriber.next(layer);
      }
    });
  }

  findLayer<LayerType extends JepLayer>(
    partialLayer: Partial<JepLayer>,
    layerType?: JepLayer['type']
  ): {
    layer: LayerType | null;
    path: { matchProperties: Partial<JepLayer>; index: number }[];
  } {
    const findLayerRecursive = (
      layers: Collection<JepLayer | JepGroupLayer>,
      path: { matchProperties: Partial<JepLayer>; index: number }[] = []
    ): {
      layer: LayerType | null;
      path: { matchProperties: Partial<JepLayer>; index: number }[];
    } => {
      for (let index = 0; index < layers.length; index++) {
        const layer = layers.getItemAt(index);

        // Handle undefined from getItemAt
        if (!layer) continue;

        const currentPath = [...path, { matchProperties: partialLayer, index }];

        // 1) Check current layer first
        const matches = Object.entries(partialLayer).every(
          ([key, value]) =>
            layer[key as keyof JepLayer & keyof JepGroupLayer] === value
        );
        const typeMatches = !layerType || layer.type === layerType;

        if (matches && typeMatches) {
          return { layer: layer as LayerType, path: currentPath };
        }

        // 2) Then recurse if group
        if ('layers' in layer) {
          const nestedResult = findLayerRecursive(
            layer.layers as Collection<JepGroupLayer | JepLayer>,
            currentPath
          );
          if (nestedResult.layer) {
            return nestedResult;
          }
        }
      }
      return { layer: null, path: [] };
    };

    const mapLayers = this.getMap()?.layers;
    if (!mapLayers) {
      return { layer: null, path: [] };
    }

    return findLayerRecursive(
      mapLayers as Collection<JepLayer | JepGroupLayer>
    );
  }

  removeLayerById<LayerType extends JepLayer>(id: string): LayerType | null {
    const layer = this.getLayerById<LayerType>(id);
    if (layer) {
      this.getMap().layers.remove(layer);
      return layer;
    }
    return null; // Explicitly return null instead of void/undefined
  }

  // Fix for setLayerVisibility method
  setLayerVisibility(
    config:
      | { ident: string; visible: boolean }
      | { ident: string; visible: boolean }[]
  ) {
    if (!config) {
      return;
    }

    const configArray = Array.isArray(config) ? config : [config];

    configArray.forEach((layerConfig) => {
      if (layerConfig?.ident === undefined) {
        return;
      }

      const layerResult = this.findLayer({ id: layerConfig.ident });
      const layer = layerResult.layer; // This is already LayerType | null

      if (layer && typeof layerConfig.visible === 'boolean') {
        layer.visible = layerConfig.visible;
      }
    });
  }

  // Update the return type to match the new plugin structure
  getMapOfLayers(): ExtendedMap<JepLayer> {
    return store.pluginMap()?.allLayers() ?? new ExtendedMap<JepLayer>();
  }

  addWidget(id: PluginID, params: WidgetParams) {
    const widgets = new ExtendedMap<WidgetParams>().concat(
      store.activeWidgetMap()
    );
    widgets.set(id, params);
    store.activeWidgetMap.set(widgets);
  }

  removeWidget(id: PluginID) {
    const widgets = store
      .activeWidgetMap()
      .filter((value: WidgetParams, key: string) => key !== id);
    store.activeWidgetMap.set(widgets);
  }

  getActiveWidgetMap(): ExtendedMap<WidgetParams> {
    return store.activeWidgetMap();
  }

  get mapReady$(): Observable<EsriMap> {
    return this._map$.pipe(
      filter((map) => !!map),
      take(1),
      shareReplay(1)
    );
  }

  getEvents(): Signal<JmkwEvent> {
    return computed(() => this._eventBusService.$event());
  }

  getEventStream(): Observable<JmkwEvent> {
    return this._eventBusService.event$;
  }

  dispatchEvent(jmkwEvent: JmkwEvent): void {
    this._eventBusService.$event.set(jmkwEvent);
  }

  getMap() {
    return store.map();
  }

  getLayerCollection(): Collection<JepLayer> {
    return store.map().layers as Collection<JepLayer>;
  }

  addLayer(layer: JepLayer): Observable<JepLayer> {
    if (!layer) {
      return throwError(() => 'Missing layer to add.');
    }

    this.getMap().add(layer);

    return from(layer.when()).pipe(
      map(() => layer),
      take(1)
    );
  }

  replaceLayer(
    replacementLayer: JepLayer,
    matchProperties: (keyof Partial<JepLayer>)[] = ['id']
  ): {
    newLayer: JepLayer | null;
    replacedLayer: JepLayer | null;
  } {
    const partialSearchLayer: Partial<JepLayer> = matchProperties.reduce(
      (acc, key) => ({
        ...acc,
        [key]: replacementLayer[key]
      }),
      {} as Partial<JepLayer>
    );

    const { layer, path } = this.findLayer(
      partialSearchLayer,
      replacementLayer.type
    );

    if (!layer) {
      return { newLayer: null, replacedLayer: null };
    }

    let layerCollection = this.getMap().layers as Collection<
      JepLayer | JepGroupLayer
    >;
    const lastPathEntry = path.pop();

    for (const entry of path) {
      const groupLayer = layerCollection.getItemAt(
        entry.index
      ) as JepGroupLayer;
      if (!groupLayer || !('layers' in groupLayer)) {
        return { newLayer: null, replacedLayer: null };
      }
      layerCollection = groupLayer.layers as Collection<
        JepLayer | JepGroupLayer
      >;
    }

    if (lastPathEntry !== undefined && layerCollection) {
      const replacedLayer = layerCollection.getItemAt(lastPathEntry.index);
      layerCollection.removeAt(lastPathEntry.index);
      layerCollection.add(replacementLayer, lastPathEntry.index);
      return {
        newLayer: replacementLayer,
        replacedLayer: replacedLayer as JepLayer
      };
    }

    return { newLayer: null, replacedLayer: null };
  }

  getMapView(): MapView {
    return store.views['2D'].view() as MapView;
  }

  getSceneView(): SceneView {
    return store.views['3D'].view() as SceneView;
  }

  getViews(): Record<EsriViewType, ViewConfig> {
    const views = store.views.unwrap();
    return views;
  }

  toggleView(esriViewType: EsriViewType): boolean {
    store.views[esriViewType].visible.update((visible: boolean) => !visible);
    return store.views[esriViewType].visible();
  }

  public moveMapToLatLong(lat: number, long: number): Promise<unknown> {
    return this.moveMapView(lat, long);
  }

  private validateCoordinates(
    latitude: number | null | undefined,
    longitude: number | null | undefined
  ): void {
    if (latitude == null || latitude > 90 || latitude < -90) {
      throw new Error('Invalid latitude: ' + latitude);
    }
    if (longitude == null || longitude > 180 || longitude < -180) {
      throw new Error('Invalid longitude: ' + longitude);
    }
  }

  public moveMapView(
    latitude: number | null | undefined,
    longitude: number | null | undefined,
    options?: GoToOptions2D
  ): Promise<unknown> {
    this.validateCoordinates(latitude, longitude);
    return this.getMapView().goTo(
      new Point({ latitude, longitude }),
      options || this._goToOptions
    );
  }

  public moveSceneView(
    latitude: number,
    longitude: number,
    options?: GoToOptions3D
  ): Promise<unknown> {
    this.validateCoordinates(latitude, longitude);
    return this.getSceneView().goTo(
      new Point({ latitude, longitude }),
      options
    );
  }

  getLatitudeShift(zoom: number) {
    const mapView = this.getMapView();
    // Derive latitude bounds based on current zoom using Web Mercator math
    const halfTileSize = 256 / 2; // base tile size factor (128)
    const mapUnitScaleFactor = (halfTileSize / Math.PI) * Math.pow(2, zoom + 1); // mercator scale for zoom
    const halfViewHeightInMapUnits = mapView.height / 2 / mapUnitScaleFactor; // vertical map units represented
    const expValue = Math.exp(Math.PI - halfViewHeightInMapUnits); // inverse mercator transform component
    let latitudeInDegrees = (Math.atan(expValue) - Math.PI / 4) * 2; // radians
    latitudeInDegrees = (latitudeInDegrees * 180) / Math.PI; // convert to degrees
    const newExtent = mapView.constraints.geometry as Extent;
    if (newExtent) {
      newExtent.ymax = latitudeInDegrees;
      newExtent.ymin = -latitudeInDegrees;
    }
    return latitudeInDegrees;
  }

  zoomTo(level: number, center?: Point) {
    const mapView = this.getMapView();

    if(center) {
      return this.executeGoTo(() => mapView.goTo({center: center, zoom: level})
      .then(() => mapView.zoom = Math.floor(level)));
    }

    const visibleExtent = webMercatorUtils.webMercatorToGeographic(
      mapView.extent
    ) as Extent;
    const yShift = this.getLatitudeShift(level);

    if (this.isPolarRegionVisible(visibleExtent)) {
      const extent = this.getAdjustedPolarExtent(visibleExtent, yShift);
      return this.executeGoTo(() => mapView.goTo(
        {target: extent, zoom: level},
        { duration: this._goToOptions.duration }
      ).then(() => mapView.zoom = Math.floor(level)));
    }

    return this.executeGoTo(() =>
      mapView.goTo({
        target: mapView.center,
        zoom: level
      }).then(() => mapView.zoom = Math.floor(level))
    );
  }

  private isPolarRegionVisible(extent: Extent): boolean {
    return extent.ymax > this._edgeOfMap || extent.ymin < -this._edgeOfMap;
  }

  private getAdjustedPolarExtent(
    visibleExtent: Extent,
    yShift: number
  ): Extent {
    const isInSouthernPolarRegion = visibleExtent.ymin < -this._edgeOfMap;
    const shiftValue = isInSouthernPolarRegion ? -yShift : yShift;

    if (!(visibleExtent instanceof Extent)) {
      throw new Error('Invalid visible extent provided');
    }

    return new Extent({
      ...visibleExtent.toJSON(),
      ymax: shiftValue,
      ymin: shiftValue
    });
  }

  private executeGoTo<T>(goToFn: () => Promise<T>): Promise<T | boolean> {
    return goToFn().catch(error => {
      const isExpected = this.isExpectedError(error);
      if (!isExpected) {
        this._logger.error('[JmkwApiService] Go-to operation failed:', error);
      }
      return isExpected;
    });
  }

  private isExpectedError(error: unknown): boolean {
    if (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      typeof (error as { name: unknown }).name === 'string'
    ) {
      const name = (error as { name: string }).name;
      return name === 'AbortError' || name.includes('goto-interrupted');
    }
    return false;
  }

  mapViewHitTestStream(): Observable<JepHitTestResult> {
    return this._hitTestService.hitTest$;
  }

  setUnitConfig(newConfig: JmkwUnitConfig) {
    Object.keys(store.env.unitConfigs).forEach((key) => {
      this.setUnit(
        key as JmkwUnitConfigKey,
        newConfig[key as JmkwUnitConfigKey]
      );
    });
  }

  setUnit(unitType: JmkwUnitConfigKey, newUnit: string) {
    store.env.unitConfigs[unitType].set?.(newUnit);
  }

  getUnitConfig() {
    return store.env.unitConfigs.unwrap();
  }

  /*******************************************************************************
   * Backgrounds and Themes (START)
   *******************************************************************************/

  changeTheme(themeId?: string) {
    this._themeService.change(themeId);
  }

  currentTheme(): Signal<ThemeInfo | undefined> {
    return this._themeService.$currentTheme;
  }

  currentTheme$(): Observable<ThemeInfo | undefined> {
    return this._themeService.currentTheme$;
  }

  getThemeConfig(): JmkwThemeConfig {
    return store.env.themeConfig.unwrap();
  }

  getThemeOptions(): ThemeInfo[] | undefined {
    return this._themeService.$enabledAvailableThemes();
  }

  onThemeChange$(): Observable<ThemeInfo | undefined> {
    return this._themeService.currentTheme$;
  }

  onBasemapChange$(): Observable<JepBasemap> {
    return this._basemapManagerService.basemapChange$;
  }

  currentBasemap$(): Observable<JepBasemap> {
    return this._basemapManagerService.currentBasemap$;
  }

  currentBasemap(): JepBasemap | null {
    return this._basemapManagerService.currentBasemap;
  }

  registerBasemap(basemap: JepBasemap) {
    return this._basemapManagerService.registerBasemap(basemap);
  }

  setBasemapById(identifier: BasemapKey): void {
    return this._basemapManagerService.setBasemapById(identifier);
  }

  availableBasemaps(): AvailableBasemaps {
    return this._basemapManagerService.availableBasemaps;
  }

  /*******************************************************************************
   * Backgrounds and Themes (END)
   *******************************************************************************/

  registerEsriErrorHandler(
    urlPattern: string,
    options?: ErrorHandlerOptions,
    handler?: EsriErrorHandler
  ): Observable<EsriRequestError> {
    return this._esriInterceptor.registerErrorHandler(
      urlPattern,
      options,
      handler
    );
  }

  unregisterEsriErrorHandler(urlPattern: string): void {
    this.removeEsriErrorHandler(urlPattern);
  }

  removeEsriErrorHandler(urlPattern: string): void {
    this._esriInterceptor.removeErrorHandler(urlPattern);
  }

  removeSpecificEsriErrorHandler(
    urlPattern: string,
    handler: EsriErrorHandler
  ): boolean {
    return this._esriInterceptor.removeSpecificErrorHandler(
      urlPattern,
      handler
    );
  }

  getEsriErrorHandlers(): Map<
    string,
    Array<{ handler: EsriErrorHandler; options: ErrorHandlerOptions }>
  > {
    return this._esriInterceptor.getErrorHandlers();
  }

  /*******************************************************************************
   * Purly Service Integration (START)
   *******************************************************************************/

  /**
   * Check if the Purly service has been successfully initialized
   */
  isPurlyInitialized(): boolean {
    return this._purlyService.isInitialized;
  }

  /**
   * Observable that emits true when the Purly service has been initialized
   */
  isPurlyInitialized$(): Observable<boolean> {
    return this._purlyService.isInitialized$;
  }

  /**
   * Get signed URLs for basemap services from Purly
   */
  getPurlyBasemapUrls(): Record<string, PurlySignedUrlInfo> {
    return this._purlyService.getBasemapUrls();
  }

  /**
   * Get signed URLs for tile services from Purly
   */
  getPurlyTileServiceUrls(): Record<string, PurlySignedUrlInfo> {
    return this._purlyService.getTileServiceUrls();
  }

  /*******************************************************************************
   * Purly Service Integration (END)
   *******************************************************************************/

  /*******************************************************************************
   * Version (START)
   *******************************************************************************/

  /**
   * Returns the current JMKW semantic version string sourced from the deployed
   * library, or null if unavailable or malformed.
   */
  getVersion(): string | null {
    if (!JMKW_VERSION || typeof JMKW_VERSION !== 'string') return null;
    if (!/^\d+\.\d+\.\d+/.test(JMKW_VERSION)) return null;
    this._logger.trace('[JmkwApiService] getVersion:', JMKW_VERSION);
    return JMKW_VERSION;
  }

  /*******************************************************************************
   * Version (END)
   *******************************************************************************/
}
